import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type FC,
    type FocusEvent,
    type MouseEvent,
    type ReactNode,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { updateUriRef } from '../../../actions/filterActions.ts';
import { formatRange, getInputRangeMinMax } from '../sbSharedRefineMenuHelpers.ts';
import { useSbSelector } from '../../../reducers/useSbSelector.ts';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { useAppliedFilterUrlLabel } from '../../helpers/appliedFilters/useAppliedFilterUrlLabel.ts';
import { type TrackEventEventArgument } from '../../../utils/tracking/searchBrowse/filterTracking.ts';

import { Button } from 'dibs-elements/exports/Button.tsx';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight.ts';
import { SbSharedLabeledInput } from '../../SbSharedLabeledInput/SbSharedLabeledInput.jsx';

import styles from './SbSharedRefineMenuInputRange.scss';

import { type SbSharedRefineMenuInputRange_itemSearch$key } from './__generated__/SbSharedRefineMenuInputRange_itemSearch.graphql.ts';
import { type SbSharedRefineMenuInputRange_filterValue$key } from './__generated__/SbSharedRefineMenuInputRange_filterValue.graphql.ts';

const messages = defineMessages({
    to: {
        id: 'sb.SbMobileRefineMenu.inputRange.to',
        defaultMessage: 'to',
    },
    error: {
        id: 'sb.SbMobileRefineMenu.inputRange.error',
        defaultMessage: 'Min value may not exceed max',
    },
    applyLabel: {
        id: 'sb.SbMobileRefineMenu.inputRange.applyLabel',
        defaultMessage: 'Apply range',
    },
    min: {
        id: 'sb.SbMobileRefineMenu.inputRange.min',
        defaultMessage: 'minimum',
    },
    max: {
        id: 'sb.SbMobileRefineMenu.inputRange.max',
        defaultMessage: 'maximum',
    },
});

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuInputRange_itemSearch on ItemSearchQueryConnection {
        ...useAppliedFilterUrlLabel_itemSearch
    }
`;

const filterFragment = graphql`
    fragment SbSharedRefineMenuInputRange_filterValue on SearchBrowseFilterValue {
        linkReference
        stats {
            key
            values
        }
    }
`;

const parseMinValue = (min: number | string | null | undefined): string | null => {
    const minParsed = Math.floor(parseInt(String(min)));
    return typeof minParsed === 'number' && !isNaN(minParsed) ? minParsed.toString() : null;
};

const parseMaxValue = (max: number | string | null | undefined): string | null => {
    const maxParsed = Math.ceil(parseInt(String(max)));
    return typeof maxParsed === 'number' && !isNaN(maxParsed) ? maxParsed.toString() : null;
};

const isInvalidRange = (
    min: string | number | null | undefined,
    max: string | number | null | undefined
): boolean => {
    return parseInt(String(min)) > parseInt(String(max)) || parseInt(String(max)) <= 0;
};

type Props = {
    itemSearch: SbSharedRefineMenuInputRange_itemSearch$key;
    filterValue: SbSharedRefineMenuInputRange_filterValue$key | null | undefined;
    labelTo?: ReactNode;
    labelFrom?: ReactNode;
    filterName?: string | null;
    additionalFilter?: string;
    statKey?: string;
    applyOnBlur?: boolean;
    showInvalidRangeErrorMessage?: boolean;
    showAnimatedPlaceholder?: boolean;
    size?: 'small' | 'medium' | 'large';
    disregardAvailableRange?: boolean | null;
    isQuickFiltersRedesignVariant?: boolean;
    trackingOptions?: Record<string, unknown>;
    ariaDescribedBy?: string;
    trackCustomRange?: boolean;
};

export const SbSharedRefineMenuInputRange: FC<Props> = ({
    itemSearch: itemSearchRef,
    filterValue: filterValueRef,
    applyOnBlur = false,
    labelFrom,
    labelTo,
    showInvalidRangeErrorMessage = true,
    size,
    ariaDescribedBy,
    isQuickFiltersRedesignVariant,
    disregardAvailableRange,
    trackCustomRange = false,
    showAnimatedPlaceholder,
    filterName,
    statKey,
    additionalFilter,
    trackingOptions,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const filterValue = useFragment(filterFragment, filterValueRef);
    const intl = useIntl();
    const dispatch = useDispatch();
    const { currency } = useCurrency();
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);

    const { linkReference, stats } = filterValue || {};
    const availableStat = stats?.find(stat => stat?.key === statKey);
    const statsValue = availableStat?.values;
    const urlLabel = useAppliedFilterUrlLabel(itemSearch, filterName);

    const { min, max, availableMin, availableMax } = getInputRangeMinMax({
        filterName,
        statsValue,
        urlLabel,
    });

    const [minValue, setMinValue] = useState<string | null>(() => parseMinValue(min));
    const [maxValue, setMaxValue] = useState<string | null>(() => parseMaxValue(max));
    const [error, setError] = useState(false);
    const [showAnimatedPlaceholderMin, setShowAnimatedPlaceholderMin] =
        useState(!!showAnimatedPlaceholder);
    const [showAnimatedPlaceholderMax, setShowAnimatedPlaceholderMax] =
        useState(!!showAnimatedPlaceholder);

    const prevMin = useRef(min);
    const prevMax = useRef(max);
    const prevShowAnimatedPlaceholder = useRef(showAnimatedPlaceholder);

    const onApply = useCallback(
        ({
            minMax,
            event,
        }: {
            minMax: [string | null, string | null];
            event: TrackEventEventArgument;
        }) => {
            let uriRef = formatRange(linkReference, minMax);
            if (statKey && additionalFilter) {
                uriRef = uriRef.replace(`{${additionalFilter}}`, statKey.toLowerCase());
            }
            dispatch(
                updateUriRef({
                    filterName,
                    uriRef,
                    ga: trackCustomRange ? { label: `custom ${filterName} range` } : {},
                    trackingOptions,
                    event,
                })
            );
        },
        [
            additionalFilter,
            dispatch,
            filterName,
            linkReference,
            statKey,
            trackCustomRange,
            trackingOptions,
        ]
    );

    useEffect(() => {
        const newMaxValue = parseMaxValue(max);
        const newMinValue = parseMinValue(min);

        if (prevShowAnimatedPlaceholder.current !== showAnimatedPlaceholder) {
            setShowAnimatedPlaceholderMin(!!showAnimatedPlaceholder);
            setShowAnimatedPlaceholderMax(!!showAnimatedPlaceholder);
            setMaxValue(newMaxValue);
            setMinValue(newMinValue);
        }

        if (prevMax.current !== max) {
            setMaxValue(newMaxValue);
        }

        if (prevMin.current !== min) {
            setMinValue(newMinValue);
        }

        // Checking both props and state validity, because both changes can invoke component update
        // depending on how range values were provided: by choosing predefined range or manually filling fields
        const hasError = isInvalidRange(min, max) || isInvalidRange(minValue, maxValue);
        setError(hasError);

        prevShowAnimatedPlaceholder.current = showAnimatedPlaceholder;
        prevMin.current = min;
        prevMax.current = max;
    }, [min, max, showAnimatedPlaceholder, minValue, maxValue]);

    const setValue = useCallback(
        (name: 'min' | 'max', value: string, event: FocusEvent<HTMLInputElement>) => {
            event.preventDefault();

            let nextMinValue = minValue;
            let nextMaxValue = maxValue;
            let shouldApply = false;

            if (name === 'min') {
                if (!disregardAvailableRange && availableMin) {
                    value = parseInt(value) < parseInt(availableMin) ? availableMin : value;
                }
                shouldApply = value !== minValue;
                nextMinValue = value;
                setMinValue(value);
                setShowAnimatedPlaceholderMin(false);
            } else {
                if (!disregardAvailableRange && availableMax) {
                    value = parseInt(value) > parseInt(availableMax) ? availableMax : value;
                }
                shouldApply = value !== maxValue;
                nextMaxValue = value;
                setMaxValue(value);
                setShowAnimatedPlaceholderMax(false);
            }

            const hasError = isInvalidRange(nextMinValue, nextMaxValue);
            setError(hasError);

            if (!hasError && shouldApply && applyOnBlur) {
                onApply({ minMax: [nextMinValue, nextMaxValue], event });
            }
        },
        [
            applyOnBlur,
            availableMax,
            availableMin,
            disregardAvailableRange,
            maxValue,
            minValue,
            onApply,
        ]
    );

    const applyValue = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            event.preventDefault();

            if (!error) {
                onApply({ minMax: [minValue, maxValue], event });
                setShowAnimatedPlaceholderMin(false);
                setShowAnimatedPlaceholderMax(false);
            }
        },
        [error, maxValue, minValue, onApply]
    );

    return (
        <>
            <div className={styles.container}>
                <SbSharedLabeledInput
                    ariaDescribedBy={ariaDescribedBy}
                    ariaLabel={labelFrom ? undefined : intl.formatMessage(messages.min)}
                    label={labelFrom}
                    size={size}
                    currency={currency}
                    value={minValue}
                    onBlur={(value: string, event: FocusEvent<HTMLInputElement>) =>
                        setValue('min', value, event)
                    }
                    dataTn="search-browse-currency-input-min"
                    name="price-min"
                    hasError={error}
                    showAnimatedPlaceholder={showAnimatedPlaceholderMin}
                    disabled={refetchInFlight && isQuickFiltersRedesignVariant}
                />
                <div className={styles.divider}>{intl.formatMessage(messages.to)}</div>
                <SbSharedLabeledInput
                    ariaDescribedBy={ariaDescribedBy}
                    ariaLabel={labelTo ? undefined : intl.formatMessage(messages.max)}
                    label={labelTo}
                    size={size}
                    currency={currency}
                    value={maxValue}
                    onBlur={(value: string, event: FocusEvent<HTMLInputElement>) =>
                        setValue('max', value, event)
                    }
                    dataTn="search-browse-currency-input-max"
                    name="price-max"
                    hasError={error}
                    showAnimatedPlaceholder={showAnimatedPlaceholderMax}
                    disabled={refetchInFlight && isQuickFiltersRedesignVariant}
                />
                {!applyOnBlur && (
                    <Button
                        ariaLabel={intl.formatMessage(messages.applyLabel)}
                        type="secondary"
                        size={size || 'large'}
                        onClick={applyValue}
                        className={styles.inputRangeButton}
                        isDangerousAction={error}
                        dataTn={`input-range-apply${error ? '-error' : ''}`}
                        disabled={refetchInFlight}
                    >
                        <ArrowRight className={styles.arrow} />
                    </Button>
                )}
            </div>
            {error && showInvalidRangeErrorMessage && (
                <div className={styles.error}>{intl.formatMessage(messages.error)}</div>
            )}
        </>
    );
};
